import dayjs, { Dayjs } from "dayjs"
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date"

export function getWeekdayAbbrev(date: string | Dayjs) {
  switch (dayjs(date).day()) {
    case 0:
      return "Su"
    case 1:
      return "M"
    case 2:
      return "T"
    case 3:
      return "W"
    case 4:
      return "Th"
    case 5:
      return "F"
    case 6:
      return "Sa"
    default:
      return ""
  }
}

/**
 * Formats a timestamp in the viewer's locale with date, hour, and minute
 * fields, intentionally omitting seconds.
 */
export function formatDateTime(timestamp: string | Date): string {
  return new Date(timestamp).toLocaleString(undefined, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

/**
 * Combines a date and a time into a single Dayjs instance.
 *
 * - Uses the `date` for the calendar portion and applies the hour/minute from `time`.
 * - Returns `null` if either `date` or `time` is missing.
 *
 * @param date A calendar date (`Dayjs` or `MaterialUiPickersDate`)
 * @param time A time value (`Dayjs` or `MaterialUiPickersDate`)
 * @returns A `Dayjs` representing the combined date and time, or `null` if inputs are incomplete.
 */
export const combineDateTime = (
  date: Dayjs | MaterialUiPickersDate,
  time: Dayjs | MaterialUiPickersDate
) => {
  if (!date || !time) return null
  return dayjs(date).hour(dayjs(time).hour()).minute(dayjs(time).minute())
}

/**
 * Adds minutes to a base datetime and returns the adjusted datetime.
 *
 * @param baseDateTime The starting `Dayjs` datetime to adjust
 * @param minutes The number of minutes to add (negative to subtract)
 * @returns The adjusted `Dayjs` datetime
 */
export function applyMinutesToDateTime(baseDateTime: Dayjs, minutes: number) {
  return baseDateTime.add(minutes, "minute")
}

/**
 * Splits a single datetime into a date (start of day) and time (full datetime).
 *
 * @param dateTime The `Dayjs` datetime to split
 * @returns An object containing `date` (start of day) and `time` (full datetime)
 */
export function splitDateAndTime(dateTime: Dayjs) {
  return {
    date: dateTime.startOf("day"),
    time: dateTime,
  }
}

/**
 * Converts a total number of minutes into days, hours, and minutes.
 *
 * @param minutes - Total number of minutes to convert.
 * @returns An object with `days`, `hours`, and `minutes` representing the same duration.
 */
export function convertMinutesToDaysHoursMinutes(minutes: number) {
  return {
    days: Math.floor(minutes / (24 * 60)),
    hours: Math.floor((minutes % (24 * 60)) / 60),
    minutes: minutes % 60,
  }
}
